import { Component, ElementRef, Renderer2 } from '@angular/core';
import { CommonModule, DatePipe, NgIf, registerLocaleData } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { LOCALE_ID } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { take } from 'rxjs/operators';

import localeEl from '@angular/common/locales/el';
import { CoreService } from '../../../services/core.service';

import { ManagePreviewActionButtonComponent } from '../components/manage-preview-action-button/manage-preview-action-button.component';
import { ManagePreviewLatestPostsComponent } from '../components/manage-preview-latest-posts/manage-preview-latest-posts.component';
import { ManagePreviewLatestAlertsComponent } from '../components/manage-preview-latest-alerts/manage-preview-latest-alerts.component';

registerLocaleData(localeEl);

@Component({
  selector: 'app-facebook-photopost',
  standalone: true,
  imports: [
    NgIf,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    ManagePreviewActionButtonComponent,
    ManagePreviewLatestPostsComponent,
    ManagePreviewLatestAlertsComponent
  ],
  providers: [
    DatePipe,
    { provide: LOCALE_ID, useValue: 'en' }
  ],
  templateUrl: './facebook-photopost.component.html',
  styleUrl: './facebook-photopost.component.scss'
})
export class FacebookPhotopostComponent {
  id: string | null = null;
  companyID: number = 0;

  siteDomain: string = 'dummy.view';
  socialTag: string = '';

  switchboardImage: number = 0;
  mainImageUrl: string = './assets/images/na.png';
  thumbnailFilePath: string = '';

  postFeed: string = '';
  postTitle: string = '';
  postDescription: string = '';
  totChars: number = 0;

  articleUrl: string = '';
  scheduledFor: Date | null = null;
  formattedDate: string = '';

  textDirection: 'ltr' | 'rtl' = 'ltr';
  postStatus: string = '';

  loading: boolean = false;

  latestPosts = [
    {
      id: 14364,
      title: 'Ζώδια Παρασκευής 22 Μαΐου 2026',
      image: 'https://www.allyou.gr/images/zodia-paraskevis-maiou-allyou-bg.jpg#joomlaImage://local-images/zodia-paraskevis-maiou-allyou-bg.jpg',
      domain: 'allyou.gr',
      platform: 'Facebook'
    },
    {
      id: 14365,
      title: 'Γάμος Taylor Swift και Travis Kelce: φήμες για ημερομηνία, τοποθεσία, καλεσμένους και όλα τα πιθανά σενάρια',
      image: 'https://www.allyou.gr/images/taylor-swift-travis-kelce-wedding-cov.jpg#joomlaImage://local-images/taylor-swift-travis-kelce-wedding-cov.jpg',
      domain: 'allyou.gr',
      platform: 'Facebook'
    },
    {
      id: 14366,
      title: 'Η Pharmakon έρχεται στην Αθήνα για μια βραδιά ηχητικής αποδόμησης',
      image: 'https://www.avopolis.gr/images/Giannis%20Papaioannou/FH010001_011.jpg',
      domain: 'avopolis.gr',
      platform: 'Facebook'
    }
  ];

  latestAlerts = [
    {
      id: 14362,
      title: 'Τύποι ιδιοσυγκρασίας παιδιού: τι σημαίνουν και πώς επηρεάζουν τη σχέση γονέα-παιδιού',
      image: 'https://childit.gr/wp-content/uploads/2026/05/idiosigrasia-paidiou-kai-goneikotita-childit-b.jpg',
      domain: 'childit.gr',
      platform: 'Facebook',
      alertTitle: 'Spike in Clicks',
      alertValue: '+86.11% (36 → 67)',
      alertType: 'positive'
    },
    {
      id: 14364,
      title: 'Ζώδια Παρασκευής 22 Μαΐου 2026',
      image: 'https://www.allyou.gr/images/zodia-paraskevis-maiou-allyou-bg.jpg#joomlaImage://local-images/zodia-paraskevis-maiou-allyou-bg.jpg',
      domain: 'allyou.gr',
      platform: 'Facebook',
      alertTitle: 'Spike in Clicks',
      alertValue: '+6314.29% (7 → 449)',
      alertType: 'positive'
    },
    {
      id: 14367,
      title: 'Τα νέα αρώματα που ξεχωρίζω τώρα: Αυτές είναι οι νέες κυκλοφορίες που μοσχοβολούν άνοιξη',
      image: 'https://www.allyou.gr/images/spring_new_fragrances_part_2_cover.jpg#joomlaImage://local-images/spring_new_fragrances_part_2_cover.jpg',
      domain: 'allyou.gr',
      platform: 'Facebook',
      alertTitle: 'Spike in Clicks',
      alertValue: '+57.14% (7 → 11)',
      alertType: 'positive'
    }
  ];

  constructor(
    private readonly coreService: CoreService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private datePipe: DatePipe,
    private elRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    const companyIdParam = this.route.snapshot.paramMap.get('company_id');
    const postIdParam = this.route.snapshot.paramMap.get('post_id');
    const idParam = this.route.snapshot.paramMap.get('id');

    this.companyID = companyIdParam
      ? Number(companyIdParam)
      : this.coreService.user?.companyID || 0;

    this.id = postIdParam || idParam;

    if (this.id) {
      this.loadPost(this.id);
    } else {
      this.coreService.showSnackbar('Post id not found.');
    }
  }

  loadPost(id: string): void {
    this.loading = true;

    this.coreService
      .getData(`/manual-post/editPost/${this.companyID}/${id}`)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          this.loading = false;

          if (!res) {
            this.coreService.showSnackbar('Post not found.');
            return;
          }

          this.textDirection = res.text_direction === 'rtl' ? 'rtl' : 'ltr';
          this.postStatus = res.post_status;
          this.socialTag = res.social_tag;

          this.siteDomain = (res.Domain || '').replace('https://www.', '');

          this.switchboardImage = res.switchboard_img != null ? 1 : 0;

          const rssMainImage =
            (res.rss_img_url && String(res.rss_img_url).trim()) || '';

          const arrayFirstImage =
            Array.isArray(res.image_array) && res.image_array.length > 0
              ? res.image_array[0]
              : '';

          this.mainImageUrl =
            this.switchboardImage === 1
              ? res.switchboard_img
              : rssMainImage || arrayFirstImage || './assets/images/na.png';

          this.thumbnailFilePath =
            res.thumbnail_file_path || './assets/images/na.png';

          this.postFeed = res.feed || '';
          this.postTitle = res.rss_title || '';

          this.postDescription = res.rss_description || '';
          this.totChars = this.postDescription.length;

          const maxChars = 250;

          this.postDescription =
            this.postDescription.length > maxChars
              ? this.postDescription.substring(0, maxChars) + '...'
              : this.postDescription;

          this.articleUrl = res.article_url || '';

          this.scheduledFor = res.scheduled_for
            ? new Date(res.scheduled_for)
            : null;

          this.formattedDate = this.scheduledFor
            ? this.datePipe.transform(this.scheduledFor, 'd MMMM YYYY') || ''
            : '';

          const element =
            this.elRef.nativeElement.querySelector('.facebook-card');

          if (element) {
            this.renderer.removeClass(element, 'hidden');
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error loading Facebook photo post:', error);
          this.coreService.showSnackbar('Failed to load post.');
        }
      });
  }

  goBack(): void {
    this.location.back();
  }

  reusePost(): void {
    if (!this.id) {
      return;
    }

    this.router.navigate(['/reuse-post', this.id]);
  }

  goToPreviousPost(): void {
    const previousPostId = this.route.snapshot.queryParamMap.get('previous');

    if (!previousPostId) {
      this.coreService.showSnackbar('Previous post not found.');
      return;
    }

    this.openPostById(previousPostId);
  }

  goToNextPost(): void {
    const nextPostId = this.route.snapshot.queryParamMap.get('next');

    if (!nextPostId) {
      this.coreService.showSnackbar('Next post not found.');
      return;
    }

    this.openPostById(nextPostId);
  }

  openLatestPost(post: any): void {
    if (!post?.id) {
      return;
    }

    this.openPostById(String(post.id));
  }

  private openPostById(postId: string): void {
    this.router.navigate([
      '/manage-preview/facebook-photopost',
      this.companyID,
      postId
    ]).then(() => {
      this.id = postId;
      this.loadPost(postId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}